import { Calculator, PiggyBank, Receipt, Timer } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { Card, ListRow, SectionHeader } from '../../components';
import { useActions } from '../../data';
import { BillSplitter } from './tools/BillSplitter';
import { ImpulseTimer } from './tools/ImpulseTimer';
import { PYFCalculator } from './tools/PYFCalculator';
import { QuickMath } from './tools/QuickMath';

type ToolKey = 'bill' | 'math' | 'impulse' | 'pyf';

const TOOLS: Array<{
  key: ToolKey;
  title: string;
  subtitle: string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
}> = [
  {
    key: 'bill',
    title: 'Bill splitter',
    subtitle: 'Tip, total, per person',
    icon: Receipt,
    iconBg: 'bg-accent/15',
    iconColor: 'text-accent',
  },
  {
    key: 'math',
    title: 'Quick math',
    subtitle: 'Add, subtract, multiply, divide',
    icon: Calculator,
    iconBg: 'bg-warning/15',
    iconColor: 'text-warning',
  },
  {
    key: 'impulse',
    title: 'Impulse timer',
    subtitle: '24-hour wait before you buy',
    icon: Timer,
    iconBg: 'bg-negative/15',
    iconColor: 'text-negative',
  },
  {
    key: 'pyf',
    title: 'Pay yourself first',
    subtitle: 'Hourly rate → savings projections',
    icon: PiggyBank,
    iconBg: 'bg-positive/15',
    iconColor: 'text-positive',
  },
];

/**
 * Tools section for Settings — four BottomSheet calculators.
 * One open at a time, controlled here.
 */
export function ToolsSection() {
  const actions = useActions();
  const [open, setOpen] = useState<ToolKey | null>(null);
  const close = () => setOpen(null);

  // Mark a tool used the moment its sheet is opened — that's enough to
  // count toward the Tool Time achievement; we don't need to detect any
  // particular interaction inside the tool.
  const openTool = (key: ToolKey) => {
    setOpen(key);
    actions.markToolUsed(key);
  };

  return (
    <div className="mb-section-gap">
      <SectionHeader title="Tools" />
      <Card flush elevation="sm" className="py-1">
        {TOOLS.map((t, i) => {
          const Icon = t.icon;
          return (
            <div key={t.key}>
              {i > 0 ? (
                <div className="h-px bg-surface-secondary mx-card-pad" />
              ) : null}
              <ListRow
                icon={<Icon size={18} strokeWidth={1.5} />}
                iconBgClass={t.iconBg}
                iconColorClass={t.iconColor}
                title={t.title}
                subtitle={t.subtitle}
                accessory="chevron"
                onClick={() => openTool(t.key)}
              />
            </div>
          );
        })}
      </Card>

      <BillSplitter open={open === 'bill'} onClose={close} />
      <QuickMath open={open === 'math'} onClose={close} />
      <ImpulseTimer open={open === 'impulse'} onClose={close} />
      <PYFCalculator open={open === 'pyf'} onClose={close} />
    </div>
  );
}
